/**
 * Leer un código de barras con la cámara (Fase 6, 0.13.0).
 *
 * Safari no tiene `BarcodeDetector`, así que el decodificador va aparte:
 * ZXing compilado a WebAssembly, que sí funciona en el iPhone. Entra con
 * import dinámico, como la librería del Excel, y su archivo `.wasm` (925 KB)
 * **no** se precarga: es lo único de la app que se baja al usarlo, y de todas
 * formas buscar el producto necesita internet.
 *
 * Cuando el navegador trae `BarcodeDetector` (Chrome y Edge en Android y
 * escritorio) se usa ese, que es más rápido y no baja nada.
 *
 * Aquí solo vive el pegamento con la plataforma: lo que decide si un código
 * sirve y qué se hace con el producto está en `domain/codigo-barras.ts`, que
 * sí se prueba.
 */

// Vite lo convierte en una URL con hash dentro de dist/assets
import urlDelWasm from 'zxing-wasm/reader/zxing_reader.wasm?url';

/** Los de los productos del súper; no se buscan QR ni otros. */
const FORMATOS = ['EAN-13', 'EAN-8', 'UPC-A', 'UPC-E'] as const;

interface DetectorDeCodigos {
  detect: (fuente: CanvasImageSource) => Promise<{ rawValue: string }[]>;
}

interface ConstructorDeDetector {
  new (opciones: { formats: string[] }): DetectorDeCodigos;
  getSupportedFormats?: () => Promise<string[]>;
}

function detectorNativo(): ConstructorDeDetector | null {
  const C = (globalThis as unknown as { BarcodeDetector?: ConstructorDeDetector }).BarcodeDetector;
  return C ?? null;
}

export type Soporte =
  /** El navegador trae el lector: no se baja nada. */
  | 'nativo'
  /** Se baja el decodificador (925 KB) la primera vez. */
  | 'wasm'
  /** Sin cámara disponible: solo se puede escribir el código. */
  | 'sin_camara';

export function soporteDeEscaner(): Soporte {
  const hayCamara =
    typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia !== undefined;
  if (!hayCamara) return 'sin_camara';
  return detectorNativo() !== null ? 'nativo' : 'wasm';
}

/** Un lector listo para mirar cuadros de video, por el camino que toque. */
export interface Lector {
  /** El código que haya en el cuadro, o null si no se ve ninguno. */
  leer: (cuadro: ImageData) => Promise<string | null>;
}

let modulo: Promise<typeof import('zxing-wasm/reader')> | null = null;

/** Carga el decodificador una sola vez y le dice dónde está su `.wasm`. */
async function cargarWasm(): Promise<typeof import('zxing-wasm/reader')> {
  modulo ??= import('zxing-wasm/reader').then((m) => {
    m.prepareZXingModule({ overrides: { locateFile: () => urlDelWasm } });
    return m;
  });
  return modulo;
}

export async function crearLector(): Promise<Lector> {
  const Nativo = detectorNativo();
  if (Nativo !== null) {
    const detector = new Nativo({ formats: [...FORMATOS].map((f) => f.toLowerCase().replace('-', '_')) });
    return {
      leer: async (cuadro) => {
        // El detector nativo quiere una fuente dibujable, no ImageData
        const lienzo = new OffscreenCanvas(cuadro.width, cuadro.height);
        lienzo.getContext('2d')?.putImageData(cuadro, 0, 0);
        const encontrados = await detector.detect(lienzo);
        return encontrados[0]?.rawValue ?? null;
      },
    };
  }

  const { readBarcodes } = await cargarWasm();
  return {
    leer: async (cuadro) => {
      const encontrados = await readBarcodes(cuadro, {
        formats: [...FORMATOS],
        maxNumberOfSymbols: 1,
        tryHarder: true,
      });
      return encontrados[0]?.text ?? null;
    },
  };
}

export type ErrorDeCamara = 'permiso' | 'sin_camara' | 'ocupada' | 'otro';

export const TEXTO_ERROR_CAMARA: Readonly<Record<ErrorDeCamara, string>> = {
  permiso:
    'No diste permiso a la cámara. Se cambia en Ajustes del iPhone → DuoFit → Cámara. También puedes escribir el código a mano.',
  sin_camara: 'Este dispositivo no tiene una cámara disponible. Escribe el código a mano.',
  ocupada: 'Otra app está usando la cámara. Ciérrala e intenta otra vez, o escribe el código a mano.',
  otro: 'No se pudo abrir la cámara. Escribe el código a mano.',
};

function motivoDelError(e: unknown): ErrorDeCamara {
  const nombre = e instanceof Error ? e.name : '';
  if (nombre === 'NotAllowedError' || nombre === 'SecurityError') return 'permiso';
  if (nombre === 'NotFoundError' || nombre === 'OverconstrainedError') return 'sin_camara';
  if (nombre === 'NotReadableError' || nombre === 'AbortError') return 'ocupada';
  return 'otro';
}

export type Camara = { tipo: 'ok'; flujo: MediaStream } | { tipo: 'error'; motivo: ErrorDeCamara };

/** Pide la cámara de atrás. Hay que llamarlo dentro de un toque. */
export async function abrirCamara(): Promise<Camara> {
  if (navigator.mediaDevices?.getUserMedia === undefined) {
    return { tipo: 'error', motivo: 'sin_camara' };
  }
  try {
    const flujo = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
      audio: false,
    });
    return { tipo: 'ok', flujo };
  } catch (e) {
    return { tipo: 'error', motivo: motivoDelError(e) };
  }
}

export function cerrarCamara(flujo: MediaStream | null): void {
  for (const pista of flujo?.getTracks() ?? []) pista.stop();
}
